<template>
	<div>
		<div v-if="stripeLoadingError">
			<div class="flex flex-col items-center justify-center gap-4">
				<ExclamationTriangleIcon class="h-10 w-10 text-red-400" />
				<strong>{{ errorInfo?.title }}</strong>
				<p class="max-w-75 text-center text-slate-500">
					{{ errorInfo?.message }}
				</p>
				<button
					type="button"
					class="border-accent-500! text-accent-500! rounded-lg border px-16 py-2"
					@click="setupStripeForm"
				>
					{{ t('document.tryAgain') }}
				</button>
			</div>
		</div>
		<div
			v-else
			class="mx-7 grid grid-cols-2 gap-2"
		>
			<div class="grid-item pr-12">
				<Summary
					:price="selectedPlanPrice"
					:interval="selectedPlanFrequencyInterval"
					:frequency="selectedPlanFrequency"
				/>
			</div>

			<div class="grid-item">
				<div class="mb-9">
					<h3 class="text-secondary-600 mb-6 font-bold">
						{{ t('oap.paymentMethod') }}
					</h3>
					<form @submit.prevent="handleSubmit">
						<h5 class="text-secondary-400 mb-2 font-medium">
							{{ t('oap.cardInformation') }}
						</h5>
						<div class="rounded-lg border">
							<div
								id="card-number-element"
								class="px-4 py-2"
								placeholder="1234 1234 1234 1234"
							/>
							<div class="flex">
								<div
									id="card-expiry-element"
									class="flex-1 border-t border-r px-4 py-2"
									placeholder="MM/YY"
								/>
								<div
									id="card-cvc-element"
									class="flex-1 border-t px-4 py-2"
									placeholder="CVC"
								/>
							</div>
						</div>

						<div
							v-if="formError"
							class="mt-2 flex items-center gap-2 text-red-400"
						>
							<ExclamationTriangleIcon class="h-4 w-4" />
							<span>{{ formError }}</span>
						</div>
					</form>
				</div>
				<div class="mb-9">
					<h3 class="text-secondary-600 mb-6 font-bold">
						{{ t('oap.billingInformation') }}
					</h3>
					<FormKit
						id="cardName"
						name="cardName"
						type="text"
						:label="t('person.fullName')"
						placeholder="Full name on card"
						v-model="name"
						label-class="$reset text-secondary-400 font-medium mb-1"
						inner-class="rounded-lg! border-slate-200! shadow-none!"
						outer-class="max-w-full!"
					/>
					<AddressInputFields v-model="address" />
				</div>
				<button
					type="submit"
					class="w-full rounded-md !bg-blue-500 py-2 !text-white hover:bg-blue-700"
					@click.prevent="handleSubmit"
				>
					{{ t('general.authorize') }}
				</button>
			</div>
		</div>
		<div class="outcome">
			<div class="error" />
		</div>
		<hr class="my-12 border-slate-200" />
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import AddressInputFields from '@/components/documents/OapStep/Sepio/AddressInputFields.vue'
import Summary from '@/components/documents/OapStep/Sepio/Summary.vue'
import { OapProvider } from '@/data/oapProviders'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { SepioPaymentPlanInterval } from '@/enums/SepioPaymentPlanInterval'
import { usePresentationLocale } from '@/i18n'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type {
	StripeBillingAddress,
	SepioCreditCard,
	SepioPaymentDetails,
} from '@/types'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { captureException } from '@sentry/vue'
import {
	loadStripe,
	type Stripe,
	type StripeCardCvcElement,
	type StripeCardExpiryElement,
	type StripeCardNumberElement,
	type StripeElements,
} from '@stripe/stripe-js'
import { computed, onMounted, ref } from 'vue'

const { t } = usePresentationLocale()

const sessionStore = useSessionStore()
const planStore = usePlanStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const { getPlanById } = useOapPlanProvider(OapProvider.SEPIO_GUARD)

const proposedInsured = computed(
	() =>
		sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		) as Plan
)
const selectedPlan = computed(() =>
	getPlanById(proposedInsured.value.oapPayment.paymentPlanId)
)
const selectedPlanPrice = computed(() => Number(selectedPlan.value?.price))
const selectedPlanFrequencyInterval = computed(() =>
	selectedPlan.value?.paymentCount === 1
		? SepioPaymentPlanInterval.Year
		: SepioPaymentPlanInterval.Month
)
const selectedPlanFrequency = computed(
	() => selectedPlan.value?.paymentCount ?? 1
)

let stripe: Stripe | null = null
let elements: StripeElements | null | undefined = null
let cardNumberElement: StripeCardNumberElement | null | undefined = null
let cardExpiryElement: StripeCardExpiryElement | null | undefined = null
let cardCvcElement: StripeCardCvcElement | null | undefined = null

const address = ref<StripeBillingAddress>({
	line1: proposedInsured.value.address,
	line2: '',
	country: 'US',
	postalCode: proposedInsured.value.zip,
	city: proposedInsured.value.city,
	state: proposedInsured.value.state,
})
const name = ref(
	`${proposedInsured.value.firstName} ${proposedInsured.value.lastName}`
)
const formError = ref()
const errorInfo = ref({ title: '', message: '' })
const stripeLoadingError = ref(false)

const setupStripeForm = async () => {
	stripeLoadingError.value = false

	try {
		stripe = await loadStripe(import.meta.env.VITE_SEPIO_STRIPE_PUBLISHABLE_KEY)
		elements = stripe?.elements()

		const style = { base: { fontSize: '16px' } }

		cardNumberElement = elements?.create('cardNumber', {
			style,
			disableLink: true,
		})
		cardExpiryElement = elements?.create('cardExpiry', { style })
		cardCvcElement = elements?.create('cardCvc', { style })

		cardNumberElement?.mount('#card-number-element')
		cardExpiryElement?.mount('#card-expiry-element')
		cardCvcElement?.mount('#card-cvc-element')
	} catch (e) {
		captureException(e, { extra: { message: 'Error loading Stripe' } })
		console.error(e)
		errorInfo.value.title = t('document.fatalTitle')
		errorInfo.value.message = t('document.fatalMessage')
		stripeLoadingError.value = true
	}

	// Default fill cardholder name and address from Proposed Insured
	name.value = `${proposedInsured.value.firstName} ${proposedInsured.value.lastName}`
	address.value = {
		line1: proposedInsured.value.address,
		line2: '',
		country: 'US',
		postalCode: proposedInsured.value.zip,
		city: proposedInsured.value.city,
		state: proposedInsured.value.state,
	}
}

const handleSubmit = async () => {
	if (!stripe || !cardNumberElement) {
		console.error('Stripe.js not loaded or card element missing')
		return
	}

	const { token, error } = await stripe.createToken(cardNumberElement, {
		name: name.value,
		address_country: address.value.country,
		address_line1: address.value.line1,
		address_line2: address.value.line2,
		address_city: address.value.city,
		address_zip: address.value.postalCode,
		address_state: address.value.state,
	})

	if (error) {
		formError.value = error.message ?? t('oap.stripeSubmissionError')
	} else if (token && token.id) {
		await setTokenOnStripeSuccess(token.id)
		formError.value = null
	}
}

const setTokenOnStripeSuccess = async (token: string) => {
	const currentPayment: SepioPaymentDetails = {
		paymentType: PlanOapPaymentType.CreditCard,
		paymentDetails: {
			token: token,
		} as SepioCreditCard,
	}

	await planStore.updatePlan(sessionDocumentsStore.currentPlanHolderID, {
		oapPayment: {
			paymentPlanId: proposedInsured.value.oapPayment.paymentPlanId,
			provider: OapProvider.SEPIO_GUARD,
			paymentPlan: currentPayment,
		},
	})

	await sessionDocumentsStore.setOapStepTo('document')
}

onMounted(async () => {
	await setupStripeForm()
})
</script>
